import { useLocation } from "react-router-dom";
import { Home } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import mainMenus from "../configs/menu";
import { useUser } from "../contexts/UserContext";
import { useEffect, useState } from "react";

type MenuItem = {
  title: string;
  url?: string;
  subItems?: MenuItem[];
};

type BreadcrumbResult = {
  items: MenuItem[]; // trail of breadcrumbs (parent → child)
  parentIndex: number;
  childIndex: number | null;
};

const Breadcrumbs = () => {
  const location = useLocation();
  const [pages, setPages] = useState<MenuItem[]>([]);
  const {user} = useUser();
  
  const findBreadcrumbs = (url: string): BreadcrumbResult | null => {
    if (url === "/")
      return null;

    // Flatten all groups → items
    const menus: MenuItem[] = mainMenus.flatMap(group => group.items);

    // Check top-level
    const mainIndex = menus.findIndex(menu => menu.url === url);
    if (mainIndex !== -1) {
      return {
        items: [menus[mainIndex]],
        parentIndex: mainIndex,
        childIndex: null,
      };
    }

    // Check sub-items
    for (let i = 0; i < menus.length; i++) {
      const subIndex = menus[i].subItems?.findIndex(sub => sub.url === url) ?? -1;
      if (subIndex !== -1) {
        return {
          items: [menus[i], menus[i].subItems![subIndex]],
          parentIndex: i,
          childIndex: subIndex,
        };
      }
    }

    return null; // not found
  };

  const [currentTime, setCurrentTime] = useState(new Date().getHours());
  useEffect(() => {
    
    const breadcrumb = findBreadcrumbs(location.pathname);
    if (breadcrumb) {
      setPages(breadcrumb.items);
    } else {
      setPages([]);
    }
  }, [location.pathname]);

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem className="hidden md:block">
          {
            location.pathname !== "/" && (
              <BreadcrumbLink href="/">
                <Home className="w-4 h-4" />
              </BreadcrumbLink>
            )
            
          }
          {
            location.pathname === "/" && (
                <BreadcrumbPage className="font-bold ">
                  {currentTime < 12 && `🌅 Good morning`}
                  {currentTime >= 12 && currentTime < 18 && "☀️ Good afternoon"}
                  {currentTime >= 18 && "🌙 Good evening"}, 
                  &nbsp;{user?.fullName}!
                </BreadcrumbPage>
            )
          }

        </BreadcrumbItem>

        {location.pathname === "/profile" && (
          <div className="flex items-center gap-2">
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Profile</BreadcrumbPage>
            </BreadcrumbItem>
          </div>
        )
        
        }
        {pages.map((page, i) => (
          <div key={i} className="flex items-center gap-2">
            <BreadcrumbSeparator />
            {i === pages.length - 1 ? (
              <BreadcrumbItem>
                <BreadcrumbPage>{page.title}</BreadcrumbPage>
              </BreadcrumbItem>
            ) : (
              <BreadcrumbItem>
                <BreadcrumbLink href={page.url}>{page.title}</BreadcrumbLink>
              </BreadcrumbItem>
            )}
          </div>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export default Breadcrumbs;
